/**
 * ssotRegistry.ts (diagnostiek) — de MELDINGEN over single-sources-of-truth.
 *
 * WAT HIER WEL EN NIET HOORT
 *
 * De catalogus — welke bronnen bestaan, wat ze nu zeggen, wie eruit leest —
 * staat op één plek: het SSOT Registry-tabblad. Dit bestand bezit die kennis
 * niet en herhaalt hem niet. Het beantwoordt de andere vraag: **wat moet er nog
 * gebeuren, en waar staan nu twee antwoorden op dezelfde vraag.**
 *
 * Tot 12 aug 2026 stond hier een tweede beschrijving van zes bronnen, met eigen
 * naam- en bestandsteksten naast die van het register. Dat is letterlijk de fout
 * die dit register moet uitbannen: twee beschrijvingen van dezelfde bron, die
 * uiteenlopen zodra iemand er één bijwerkt. De bewakingen leven nu op de entries
 * zelf (`SsotEntry.guard`); dit bestand draait ze en leent naam en bestandspad
 * uit de entry.
 *
 * TWEE SOORTEN MELDINGEN, STRENG GESCHEIDEN
 *
 *   - `drift`     — een bewaking op een entry is omgeslagen. Dit is BEWIJS: er
 *                   bestaat nu werkelijk een tweede definitie.
 *   - `open`      — een bekende, gedateerde openstaande drift die de browser
 *                   niet kan meten (bijvoorbeeld: hoeveel modules hun eigen
 *                   motion-varianten definiëren). Dit is een CLAIM.
 *
 * Een `open`-item mag NOOIT groen tonen. Het is per definitie onbewezen, en een
 * scherm dat onbewezen items als "in orde" presenteert, is de volgende generatie
 * van het probleem dat het moest oplossen.
 *
 * REGEL VOOR NIEUWE ITEMS: geen item zonder datum en zonder concreet gevolg.
 * "Ooit opschonen" is geen bevinding.
 */

import { getSsotRegistry } from '../../../../governance';
import type { SsotEntry } from '../../../../governance';
import { getPlatformSsotEntries } from '../ssot/platformSsotEntries';
import { BREAK_GLASS_MINUTES } from '../../../../data/supabase/dataAccess';

export type SsotStatus = 'ok' | 'drift' | 'open';

export interface SsotCheck {
  id: string;
  /** Wat er precies één keer hoort te bestaan. */
  concern: string;
  /** Het bestand dat de bron IS. */
  source: string;
  /**
   * `verified`: de status komt uit een live vergelijking op de register-entry.
   * `declared`: de status is een gedateerde bevinding, niet gemeten.
   */
  kind: 'verified' | 'declared';
  status: SsotStatus;
  /** Wat een beheerder hieraan heeft — gevolg, niet mechaniek. */
  detail: string;
}

/** Alle gecatalogiseerde bronnen: governance-laag + platform-laag. */
function allEntries(): SsotEntry[] {
  return [...getSsotRegistry(), ...getPlatformSsotEntries()];
}

/**
 * Draait elke bewaking die het register kent.
 *
 * De naam en het bestandspad komen uit de entry, niet uit dit bestand — zo kan
 * een melding niet iets anders heten dan de bron waar hij over gaat.
 */
function runGuards(): SsotCheck[] {
  return allEntries().filter(entry => typeof entry.guard === 'function').map(entry => {
    const result = entry.guard!();
    return {
      id: entry.id,
      concern: entry.name,
      source: `${entry.file} › ${entry.exportName}`,
      kind: 'verified' as const,
      status: result.ok ? ('ok' as const) : ('drift' as const),
      detail: result.detail
    };
  });
}

/**
 * Bekende, nog niet opgeloste drifts. Deze staan hier omdat ze zichtbare schuld
 * zijn, niet omdat ze meetbaar zijn — en juist daarom horen ze NIET in het
 * register: dat catalogiseert bronnen die bestaan, niet bronnen die er hadden
 * moeten zijn.
 */
const DECLARED: SsotCheck[] = [{
  id: 'motion-variants',
  concern: 'Motion-varianten (containerVariants / itemVariants)',
  source: 'nog geen — kandidaat: config/motion.tokens.ts',
  kind: 'declared',
  status: 'open',
  detail: 'Minstens vijf bronnen: portals/admin/shared, portals/teacher-v2/shared, DashboardCharts (inline), components/shared/animations.ts en useMotionBudget. Bewust nog niet samengevoegd: admin gebruikt de toestandsnaam "show", teacher-v2 "visible". Wie ze samenvoegt zonder dat mee te nemen, krijgt schermen die stil niet meer animeren — framer-motion meldt een ontbrekende variantnaam niet. Vraagt per scherm visuele controle (APP_FEEL_2027_UPGRADE §W13).'
}, {
  id: 'pseudonym-server-parity',
  concern: 'Pseudoniem serverzijde vs. clientzijde',
  source: 'portals/shared/learnerPseudonym.ts (client) — 0025 admin_list_accounts (server)',
  kind: 'declared',
  status: 'open',
  detail: 'De RPC rekent met SHA-256, de client met een rolling hash. Opgelost voor wat een beheerder ZIET (de client labelt alles), maar de twee algoritmes bestaan nog naast elkaar. Zolang dat zo is, levert een rechtstreekse query op de database andere labels dan het scherm.'
}, {
  id: 'orphan-file-sweep',
  concern: 'Bestanden zonder importeurs',
  source: 'nog geen geautomatiseerde controle',
  kind: 'declared',
  status: 'open',
  detail: 'Vier keer op 12 aug 2026 bleek een als "verwijderd" gedocumenteerd bestand nog te bestaan met nul importeurs — waaronder een compleet tweede inlogpad (525 regels) en de demo-map (8 bestanden). De export was telkens weg, het bestand niet. Zonder controle op nul-importeurs is "verwijderd" een intentie die niemand tegenspreekt.'
}, {
  id: 'break-glass-server-default',
  concern: 'Server-default van request_data_access',
  source: 'data/supabase/dataAccess.ts (client) — 0008 request_data_access (server)',
  kind: 'declared',
  status: 'open',
  detail: `De client stuurt altijd ${BREAK_GLASS_MINUTES} minuten mee, maar de RPC heeft zelf een default van 60. Nu onschadelijk; wél een valstrik voor elke andere aanroeper (SQL-console, een toekomstige tool) die de parameter weglaat en stil een heel uur toegang krijgt. Op te lossen met één migratie die de server-default gelijktrekt.`
}, {
  id: 'governance-states-unsurfaced',
  concern: 'Verificatie- en toestemmingsstatus in de accountlijst',
  source: 'server: 0020 teacher_status, 0005 consent_status — client: adminDirectory.DirectoryAccount',
  kind: 'declared',
  status: 'ok',
  detail: 'Aangesloten op 16 aug 2026. `AccountStatusNotice` toont verificatie- en toestemmingsstatus in het detailpaneel, en `ServerDirectoryPanel` telt de leerkrachten die op verificatie wachten. Bewust alleen zichtbaar wanneer er iets aan de hand is: een melding die meestal "geen bijzonderheden" zegt, wordt niet meer gelezen op het moment dat het wél telt. Nog open: verifiëren zelf (`verify_teacher`, 0020) heeft nog geen knop.'
}, {
  id: 'inline-date-formatting',
  concern: 'Datumnotatie in adminschermen',
  source: 'utils/formatters.ts › formatDateShort / formatDateLong / formatDateWithTime',
  kind: 'declared',
  status: 'open',
  detail: 'Zes adminschermen (o.a. GovernanceSettingsTab, GraceLedgerSection, CompareSnapshotsTab, OverviewDashboard) roepen `toLocaleDateString(\'nl-NL\', …)` aan met hun eigen optie-objecten, terwijl de gedeelde formatters bestaan en elders in admin al gebruikt worden. Zelfde uitkomst vandaag; het risico is dat hetzelfde soort tijdstip per scherm anders gaat lezen ("22 apr" vs "22 april 2026").'
}, {
  id: 'page-size-default',
  concern: 'Aantal rijen per pagina in lijstschermen',
  source: 'nog geen — kandidaat: config/constants.ts › DEFAULT_PAGE_SIZE',
  kind: 'declared',
  status: 'open',
  detail: 'Vijf verschillende getallen: DataTable 50, UsersPage 25, QuestionsTab 50 (eigen paginering), LogsPage 50, teacher-v2 AssessmentsPage 20. Cosmetisch vandaag; het echte risico is dat een volgend gedeeld lijstcomponent de verkeerde default overneemt en lijsten per scherm anders lang aanvoelen.'
}, {
  id: 'minimum-attempts-scatter',
  concern: 'Hoeveel pogingen voordat een uitspraak mag',
  source: 'drie losse constanten, geen gedeelde theorie',
  kind: 'declared',
  status: 'open',
  detail: 'MIN_ATTEMPTS_FOR_MASTERED (3), minAttemptsForDecay (3) en MASTERY_CONFIDENCE_MIN_ATTEMPTS (5) beantwoorden alle drie "wanneer weten we genoeg", met twee verschillende getallen. Elk is gedocumenteerd als bewust apart (UI-status vs. verval vs. adaptieve zekerheid), maar er is geen bron die uitlegt waarom 3 en 5 naast elkaar bestaan. Vraagt een didactisch besluit, geen refactor — zie MASTERY_DIDACTISCH_AUDIT_2026.'
}];

/**
 * Alle meldingen: de uitkomst van elke live bewaking, plus de vastgelegde schuld.
 *
 * Het diagnostiek-scherm toont de geslaagde bewakingen niet als lijst — dat is de
 * catalogus, en die staat in SSOT Registry. Het toont het AANTAL geslaagde
 * bewakingen (bewijs dat er gemeten is) en de inhoud van wat niet klopt.
 */
export function runSsotChecks(): SsotCheck[] {
  return [...runGuards(), ...DECLARED];
}

/** Aantal bronnen dat op dit moment een live bewaking heeft. */
export function getGuardedSourceCount(): number {
  return allEntries().filter(e => typeof e.guard === 'function').length;
}
